import { Component, Input, ViewChild } from "@angular/core";
import { ColumnFilter } from "primeng/table";
import ColumnFilterSpaceZoneComponentTemplate from "./column-filter-space-zone.component.html";
import { EventSpaceZone } from "../../models/events/guests/event-space-zone.model";
import {
  SpaceOption,
  SpaceZoneFilterValue,
  ZonePlacesMap,
  buildZonePlacesKey
} from "../../models/events/space-zone-filter.model";

@Component({
  selector: "d-column-filter-space-zone",
  template: ColumnFilterSpaceZoneComponentTemplate,
  styles: [
    `
    .space-zone-filter {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      min-width: 16rem;
    }

    .clear-filter-button {
      align-self: flex-start;
    }
    `
  ]
})
export class ColumnFilterSpaceZoneComponent {
  @ViewChild(ColumnFilter) columnFilter: ColumnFilter;

  @Input() field: string;
  @Input() spaces: SpaceOption[] = [];
  @Input() zones: EventSpaceZone[] = [];
  @Input() zonePlacesMap: ZonePlacesMap = {};
  @Input() filtersOnRow: boolean = false;
  @Input() spacePlaceholder: string = "events.spaces.select_space";
  @Input() zonePlaceholder: string = "events.spaces.select_zone";
  @Input() placePlaceholder: string = "events.spaces.select_place";

  onSpaceChange(
    spaceId: number | null,
    currentValue: SpaceZoneFilterValue | undefined,
    filterCallback: (value: SpaceZoneFilterValue | null) => void
  ) {
    const nextValue: SpaceZoneFilterValue = {};

    if (spaceId !== null && spaceId !== undefined) {
      nextValue.spaceId = spaceId;

      if (
        currentValue?.zoneId !== undefined &&
        currentValue.zoneId !== null &&
        this.getSpaceIdFromZoneId(currentValue.zoneId) === spaceId
      ) {
        nextValue.zoneId = currentValue.zoneId;

        if (
          currentValue.placeKey &&
          this.isPlaceInZone(currentValue.placeKey, currentValue.zoneId)
        ) {
          nextValue.placeKey = currentValue.placeKey;
        }
      }
    }

    this.emitFilter(nextValue, filterCallback);
    this.closeFilterOverlay();
  }

  onZoneChange(
    zoneId: number | null,
    currentValue: SpaceZoneFilterValue | undefined,
    filterCallback: (value: SpaceZoneFilterValue | null) => void
  ) {
    const nextValue: SpaceZoneFilterValue = {};

    if (zoneId !== undefined) {
      nextValue.zoneId = zoneId;
    }

    const derivedSpaceId = zoneId !== null && zoneId !== undefined ? this.getSpaceIdFromZoneId(zoneId) : null;

    if (derivedSpaceId !== null && derivedSpaceId !== undefined) {
      nextValue.spaceId = derivedSpaceId;
    } else if (currentValue?.spaceId !== undefined && zoneId === null) {
      nextValue.spaceId = currentValue.spaceId;
    }

    this.emitFilter(nextValue, filterCallback);
    this.closeFilterOverlay();
  }

  onPlaceChange(
    placeKey: string | null,
    currentValue: SpaceZoneFilterValue | undefined,
    filterCallback: (value: SpaceZoneFilterValue | null) => void
  ) {
    const nextValue: SpaceZoneFilterValue = {};

    if (currentValue?.spaceId !== undefined) {
      nextValue.spaceId = currentValue.spaceId;
    }
    if (currentValue?.zoneId !== undefined) {
      nextValue.zoneId = currentValue.zoneId;
    }
    if (placeKey) {
      nextValue.placeKey = placeKey;
    }

    this.emitFilter(nextValue, filterCallback);
    this.closeFilterOverlay();
  }

  clear(filterCallback: (value: SpaceZoneFilterValue | null) => void) {
    filterCallback(null);
    this.closeFilterOverlay();
  }

  getZonesForSpace(spaceId: number | null | undefined): EventSpaceZone[] {
    if (spaceId === null || spaceId === undefined) {
      return this.zones;
    }
    return this.zones.filter(zone => this.getSpaceIdFromZone(zone) === spaceId);
  }

  getPlacesForZone(zoneId: number | null | undefined) {
    if (zoneId === undefined) {
      return [];
    }
    return this.zonePlacesMap[buildZonePlacesKey(zoneId)] || [];
  }

  private getSpaceIdFromZoneId(zoneId: number): number | null {
    const zone = this.zones.find(z => z.id === zoneId);
    return zone ? this.getSpaceIdFromZone(zone) : null;
  }

  private getSpaceIdFromZone(zone: EventSpaceZone): number | null {
    return zone.fK_events_spaces_zones_events_spaces?.id ?? zone.event_space_id ?? null;
  }

  private emitFilter(partial: SpaceZoneFilterValue, callback: (value: SpaceZoneFilterValue | null) => void) {
    const hasSpace = partial.spaceId !== undefined;
    const hasZone = partial.zoneId !== undefined;
    const hasPlace = partial.placeKey !== undefined && partial.placeKey !== null;

    if (!hasSpace && !hasZone && !hasPlace) {
      callback(null);
      return;
    }

    const value: SpaceZoneFilterValue = {};
    if (hasSpace) {
      value.spaceId = partial.spaceId ?? null;
    }
    if (hasZone) {
      value.zoneId = partial.zoneId ?? null;
    }
    if (hasPlace) {
      value.placeKey = partial.placeKey ?? null;
    }

    callback(value);
  }

  private isPlaceInZone(placeKey: string, zoneId: number): boolean {
    const places = this.getPlacesForZone(zoneId) || [];
    return places.some(place => place.key === placeKey);
  }

  private closeFilterOverlay(): void {
    if (this.columnFilter) {
      this.columnFilter.overlayVisible = false;
    }
  }
}
